import { useState } from "react";
import ScrollToTop from "@/components/feature/ScrollToTop";
import BackToTop from "@/components/feature/BackToTop";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import { debunkList } from "@/mocks/debunk";

const cats = ["全部", ...Array.from(new Set(debunkList.map((d) => d.category)))];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      name: "辟谣汇总 · 瓜田爆料",
      url: `${SITE_ORIGIN}/debunk`,
      description:
        "瓜田爆料辟谣汇总：收录已出现官方回应或硬反证的传闻条目，逐条说明传闻内容、判定结果与澄清来源。",
      inLanguage: "zh-CN",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "辟谣汇总", item: `${SITE_ORIGIN}/debunk` },
      ],
    },
    {
      "@type": "ItemList",
      name: "已澄清传闻清单",
      numberOfItems: debunkList.length,
      itemListElement: debunkList.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
      })),
    },
  ],
};

export default function Debunk() {
  const [cat, setCat] = useState("全部");
  const filtered = cat === "全部" ? debunkList : debunkList.filter((d) => d.category === cat);

  useSeo({
    title: "辟谣汇总 | 已澄清传闻与官方回应 · 瓜田爆料",
    description:
      "辟谣汇总收录已被官方或当事人澄清的传闻，逐条说明传闻原文、判定结果与澄清来源，转发之前先查一遍，避免二次传播。",
    keywords: ["辟谣汇总", "已澄清传闻", "官方回应", "真假鉴定", "可信度评估"],
    path: "/debunk",
    ogType: "website",
    jsonLd,
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <ScrollToTop />
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-secondary-50 paper-texture">
          <div className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "辟谣汇总" }]} />
            <h1 className="mt-5 font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              辟谣汇总
            </h1>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
              这里的每一条都出现了官方回应、当事人说明或可核对的反证。
              如果你正准备转发某条消息，不妨先搜一下它在不在这个列表里。
            </p>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-xs text-foreground-500">
              <span>累计澄清 <strong className="font-mono-num text-foreground-900">{debunkList.length}</strong> 条</span>
              <span>覆盖类别 <strong className="font-mono-num text-foreground-900">{cats.length - 1}</strong> 类</span>
              <span>每条均标注澄清来源</span>
            </div>
          </div>
        </section>

        <section className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="flex flex-wrap items-center gap-2 pb-4 border-b border-background-200">
            {cats.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCat(item)}
                className={`px-4 py-2 rounded-control text-sm transition-colors whitespace-nowrap ${
                  cat === item
                    ? "bg-secondary-500 text-background-50 font-medium"
                    : "text-foreground-600 hover:bg-background-100"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <ul className="mt-6 space-y-4">
            {filtered.map((item) => (
              <li
                key={item.id}
                className="border border-background-200 rounded-card bg-background-50 overflow-hidden"
              >
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 bg-background-100 border-b border-background-200">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-control bg-secondary-500 text-background-50 text-[11px] font-medium">
                    <i className="ri-shield-check-line"></i>
                    判定：不实 / 误读
                  </span>
                  <span className="flex items-center gap-4 text-[11px] text-foreground-500">
                    <span className="px-2 py-0.5 rounded-control bg-background-200">{item.category}</span>
                    <span className="font-mono-num">{item.date}</span>
                  </span>
                </div>
                <div className="p-5">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 leading-snug">
                    {item.title}
                  </h2>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-control border border-background-200 bg-background-100 p-4">
                      <p className="text-xs font-medium text-foreground-500 mb-2 flex items-center gap-1.5">
                        <i className="ri-question-line"></i>
                        传闻内容
                      </p>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.rumor}</p>
                    </div>
                    <div className="rounded-control border border-secondary-200 bg-secondary-50 p-4">
                      <p className="text-xs font-medium text-secondary-700 mb-2 flex items-center gap-1.5">
                        <i className="ri-check-double-line"></i>
                        核实结果
                      </p>
                      <p className="text-sm text-foreground-700 leading-relaxed">{item.result}</p>
                    </div>
                  </div>
                  <p className="mt-4 flex items-center gap-2 text-xs text-secondary-700">
                    <i className="ri-verified-badge-line"></i>
                    澄清来源：{item.officialSource}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          {filtered.length === 0 && (
            <div className="mt-10 border border-dashed border-background-300 rounded-card p-12 text-center">
              <p className="text-sm text-foreground-500">该类别下暂时没有已澄清条目。</p>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}